import React, { useEffect, useState } from 'react';
import { Drawer, Form, Select, Button, DatePicker, message, Spin, Modal } from 'antd';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { fetchODs } from '../TemplatesAccommodations/actions';
import { getEmployees } from '../TemplatesAccommodations/selectors';
import { api, showSuccessNotification, showErrorNotification } from '@med-fe/util';
import { saveAssignment } from './ramps-actions';

interface Props {
  open: boolean;
  onClose: () => void;
  modalData?: any;
}

const AddRampAssignmentDrawer: React.FC<Props> = ({ open, onClose, modalData }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const employees = useSelector(getEmployees);
  const [providers, setProviders] = useState<any[]>([]);
  const [ramps, setRamps] = useState<any[]>([]);
  const [loadingRamps, setLoadingRamps] = useState(false);
  const [filteredOffices, setFilteredOffices] = useState<any[]>([]);

  useEffect(() => {
    if (open) {
      // trigger existing ODs fetch for global store (keeps other parts working)
      try {
        dispatch(fetchODs());
      } catch (e) {
        // ignore
      }

      // fetch ramps basic details
      setLoadingRamps(true);
      api
        .get('/ramps/basic-details', true, false)
        .then((res: any) => setRamps(Array.isArray(res) ? res : []))
        .catch((err: any) => console.error('Failed to fetch ramps', err))
        .finally(() => setLoadingRamps(false));
      // providers will be populated from global store via fetchODs saga
      // if modalData provided (edit), populate form
      if (modalData) {
        try {
          const initial: any = {};
          if (modalData.RampId || modalData.rampId || modalData.Ramp)
            initial.ramp = String(modalData.RampId || modalData.rampId || modalData.Ramp);
          if (modalData.Provider || modalData.provider || modalData.employeeId || modalData.EmployeeId)
            initial.provider = String(
              modalData.Provider || modalData.provider || modalData.employeeId || modalData.EmployeeId
            );
          if (
            modalData.OfficeIds ||
            modalData.officeIds ||
            modalData.OfficeId ||
            modalData.officeId ||
            modalData.Locations ||
            modalData.locations
          ) {
            const locs =
              modalData.OfficeIds ||
              modalData.officeIds ||
              (modalData.OfficeId ? [modalData.OfficeId] : null) ||
              modalData.Locations ||
              modalData.locations ||
              [];
            initial.locations = Array.isArray(locs) ? locs.map((v: any) => String(v)) : [String(locs)];
          }
          if (modalData.StartDate || modalData.startDate)
            initial.startDate = moment(modalData.StartDate || modalData.startDate);
          form.setFieldsValue(initial);
        } catch (e) {
          // ignore mapping errors
        }
      }
    } else {
      form.resetFields();
      setFilteredOffices([]);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setProviders(Array.isArray(employees) ? employees : []);
    } else {
      setProviders([]);
    }
  }, [employees, open]);

  // when editing, wait for providers to populate then set provider-related fields and filtered offices
  useEffect(() => {
    if (!open || !modalData || !providers || providers.length === 0) return;

    const providerIdStr = String(
      modalData.ProviderId ||
        modalData.Provider ||
        modalData.provider ||
        modalData.employeeId ||
        modalData.EmployeeId ||
        ''
    );
    if (!providerIdStr) return;

    const prov = providers.find((p: any) => String(p.id) === providerIdStr);
    if (prov && Array.isArray(prov.locations)) {
      setFilteredOffices(prov.locations);
      // map incoming OfficeId(s) to locationNumber(s) for UI selection
      const locationsArr =
        modalData.OfficeIds ||
        modalData.officeIds ||
        (modalData.OfficeId ? [modalData.OfficeId] : null) ||
        modalData.Locations ||
        modalData.locations;
      let initialLocations: string[] = [];
      if (Array.isArray(locationsArr)) {
        initialLocations = locationsArr
          .map((oid: any) => {
            const found = prov.locations.find((loc: any) => String(loc.OfficeId || loc.officeId) === String(oid));
            return found ? String(found.locationNumber ?? found.LocationNumber ?? found.locationNumber) : null;
          })
          .filter(Boolean) as string[];
      } else if (prov.locations.length === 1) {
        initialLocations = [
          String(prov.locations[0].locationNumber ?? prov.locations[0].LocationNumber ?? prov.locations[0].OfficeId),
        ];
      }

      form.setFieldsValue({ provider: providerIdStr, locations: initialLocations });
    } else {
      form.setFieldsValue({ provider: providerIdStr });
    }
  }, [modalData, providers, open]);

  const onProviderChange = (value: any, option: any) => {
    if (!option) {
      setFilteredOffices([]);
      form.resetFields(['locations']);
      return;
    }

    // value is provider id (string)
    const providerId = String(value);
    const prov = providers.find((p: any) => String(p.id) === providerId);
    if (prov && Array.isArray(prov.locations)) {
      // prov.locations contain OfficeId and locationNumber
      setFilteredOffices(prov.locations);
      // if only one location available for this provider, pre-populate it
      if (prov.locations.length === 1) {
        const o = prov.locations[0];
        const locVal = String(o.locationNumber ?? o.LocationNumber ?? o.OfficeId ?? o.officeId);
        form.setFieldsValue({ locations: [locVal] });
      } else {
        // clear any previously selected single-location value when multiple locations available
        form.resetFields(['locations']);
      }
    } else {
      setFilteredOffices([]);
      form.resetFields(['locations']);
    }
  };

  const disabledDate = (current: any) => {
    return current && current.startOf('day').isBefore(moment().startOf('day'));
  };

  const [saving, setSaving] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState<any>(null);

  // Show confirmation dialog before saving
  const handleFormFinish = (values: any) => {
    if (!values.ramp || !values.provider || !values.locations || !values.startDate) {
      message.error('Please fill required fields');
      return;
    }
    setPendingValues(values);
    setConfirmVisible(true);
  };

  // Actually save after confirmation
  const handleConfirmOk = async () => {
    if (!pendingValues) return;
    const values = pendingValues;
    setConfirmVisible(false);
    setPendingValues(null);

    const rampId = Number(values.ramp);
    const employeeId = Number(values.provider);
    // map selected location values (which are locationNumber strings) back to OfficeId when possible
    const officeIds = (values.locations || [])
      .map((v: any) => {
        if (v === null || v === undefined) return null;
        const val = String(v);
        // try to find matching office in filteredOffices
        const found = (filteredOffices || []).find((loc: any) => {
          return (
            String(loc.locationNumber ?? loc.LocationNumber ?? loc.locationNumber) === val ||
            String(loc.OfficeId ?? loc.officeId) === val
          );
        });
        if (found) return Number(found.OfficeId ?? found.officeId ?? found.locationNumber);
        const n = Number(val);
        return Number.isNaN(n) ? null : n;
      })
      .filter((x) => x !== null);

    // For edit, do not send employeeId and officeIds as per requirement
    let payload: any = {
      ...(modalData && modalData.RampAssignmentId ? { rampAssignmentId: modalData.RampAssignmentId } : {}),
      rampId,
      startDate: values.startDate.format('YYYY-MM-DD'),
    };

    if (!(modalData && modalData.RampAssignmentId)) {
      payload = { ...payload, officeIds, employeeId };
    }

    setSaving(true);
    try {
      await dispatch(saveAssignment(payload));
    } catch (e) {}
    setSaving(false);
    onClose();
  };

  const handleConfirmCancel = () => {
    setConfirmVisible(false);
    setPendingValues(null);
  };

  const drawerTitle = modalData ? 'Edit Ramp Assignment' : 'Add Ramp Assignment';

  return (
    <Drawer title={drawerTitle} placement='right' onClose={saving ? undefined : onClose} open={open} width={420}>
      <Form form={form} layout='vertical' onFinish={handleFormFinish}>
        <Form.Item label={'Ramp'} name='ramp' rules={[{ required: true, message: 'Ramp is required' }]}>
          {loadingRamps ? (
            <Spin />
          ) : (
            <Select
              placeholder='Select Ramp'
              options={ramps.map((r: any) => ({ value: String(r.RampId), label: r.Name }))}
            />
          )}
        </Form.Item>

        <Form.Item label={'Provider'} name='provider' rules={[{ required: true, message: 'Provider is required' }]}>
          <Select
            showSearch
            placeholder='Select Provider'
            filterOption={(input, option: any) => {
              const val = option?.label || option?.value || '';
              return String(val).toUpperCase().indexOf(String(input).toUpperCase()) !== -1;
            }}
            options={providers.map((emp: any) => {
              const num = emp.employeeNumber;
              return {
                value: String(emp.id),
                label: num ? `${emp.name} (${num})` : emp.name,
                key: String(emp.id),
              };
            })}
            onChange={onProviderChange}
            disabled={!!modalData}
          />
        </Form.Item>

        <Form.Item label={'Location(s)'} name='locations' rules={[{ required: true, message: 'Location is required' }]}>
          <Select
            allowClear
            showArrow
            mode={'multiple'}
            maxTagCount={'responsive'}
            showSearch
            placeholder='Select locations'
            options={filteredOffices.map((o: any) => ({
              // use locationNumber as the value so UI is consistent with Add flow
              value: String(o.locationNumber ?? o.LocationNumber ?? o.OfficeId ?? o.officeId),
              label: `${o.locationName} (${o.locationNumber ?? o.LocationNumber ?? ''})`,
              key: String(o.locationNumber ?? o.LocationNumber ?? o.OfficeId ?? o.officeId),
            }))}
            filterOption={(input, option: any) => {
              const val = option?.label || option?.value || '';
              return String(val).toUpperCase().indexOf(String(input).toUpperCase()) !== -1;
            }}
            disabled={!!modalData}
          />
        </Form.Item>

        <Form.Item
          label={'Start Date'}
          name='startDate'
          rules={[{ required: true, message: 'Start date is required' }]}
        >
          <DatePicker disabledDate={disabledDate} style={{ width: '100%' }} />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type='primary' htmlType='submit' loading={saving} disabled={saving}>
            Save
          </Button>
        </div>
        <Modal
          open={confirmVisible}
          onOk={handleConfirmOk}
          onCancel={handleConfirmCancel}
          okText='OK'
          cancelText='Cancel'
          title='Confirm Ramp Assignment'
          maskClosable={false}
        >
          <div>
            The current ramp will be applied immediately. Please review them carefully. Click OK to proceed or Cancel to
            make changes.
          </div>
        </Modal>
      </Form>
    </Drawer>
  );
};

export default AddRampAssignmentDrawer;
